import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormLayoutComposer, IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import { customTabProps, ITabProps } from 'components/common/form/layout/tab';
import useTemplateFormTools from 'components/common/form/hooks/useTemplateFormTools';
import { FormElementComponent } from 'components/common/form/element/FormElementComponent';
import Variables from 'app/concierge/form/secure-json-editor/Variables';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { IVariable } from 'app/concierge/form/secure-json-editor';
import { FormTabContent } from 'components/common/form/layout/tab/FormTabContent';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import { prepareFormElementProps } from 'components/common/form/formTools';
import { RawFormComponentType } from 'components/common/form';
import IconMi from 'components/common/icon/IconMi';
import StaticInfo from 'components/common/StaticInfo';
import useBundleTranslation from 'i18n';
import { useFormState } from 'react-hook-form';
import { conciergeAPI, ITestConnectionResponse } from 'api/concierge';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { ActSubmit } from 'components/common/form/act';
import { useHasTokenString } from 'hooks/useHasTokenString';
import parse from 'html-react-parser';
import showdown from 'showdown';

const markdownConverter = new showdown.Converter({ simpleLineBreaks: true, openLinksInNewWindow: true });

export default function ConciergeCustomServiceForm(props: IFormLayoutProps) {
    const { t } = useBundleTranslation(['app/admin/concierge/custom-service']);

    const findComponent = (components: RawFormComponentType[], name: string): RawFormComponentType | null => {
        for (const component of components) {
            if (component.name === name) {
                return component;
            }
            if (component.components && component.components.length > 0) {
                const found = findComponent(component.components, name);
                if (found) return found;
            }
        }
        return null;
    };

    const getTokenConfig = useMemo(() => {
        const tabs = props.hookForm.form.tabs;
        if (!tabs?.formTabs) return null;

        for (const tab of Object.values(tabs.formTabs)) {
            const component = findComponent(tab.components, 'get_token');
            if (component) {
                return component;
            }
        }

        return null;
    }, [props]);

    const configurationTab = (
        <ConfigurationTab {...customTabProps(props, 'configuration')} getTokenConfig={getTokenConfig} />
    );

    const customProps = {
        ...props,
        customTabs: { configuration: configurationTab },
    };

    const scrollToJsonField = async () => {
        const tabs = props.hookForm.form.tabs;
        if (!tabs?.formTabs) return;

        for (const tab of Object.values(tabs.formTabs)) {
            const component = findComponent(tab.components, 'json');
            if (component) {
                if (!tab.isActive && tabs) {
                    const tabIndex = tabs.formTabs.findIndex((t) => t.name === tab.name);
                    if (tabIndex !== -1) {
                        tabs.handleTabChange(true, tabIndex);
                        await new Promise((resolve) => setTimeout(resolve, 100));
                    }
                }
                break;
            }
        }
    };

    props.hookForm.form.formAct.overrideAct(
        'submit',
        class extends ActSubmit {
            public async exec(data: any): Promise<any> {
                const jsonValue = props.hookForm.form.hookFormGetValues('json');
                if (jsonValue) {
                    try {
                        JSON.parse(jsonValue);
                    } catch (e) {
                        const errorMessage = t('error_cant_parse_json');
                        props.hookForm.form.hookFormSetError('json', {
                            type: 'validation',
                            message: errorMessage,
                        });
                        scrollToJsonField();
                        return false;
                    }
                }

                return super.exec(props.hookForm.form.hookFormGetValues());
            }
        },
    );

    return <FormLayoutComposer props={customProps} />;
}

interface IConfigurationTabProps extends ITabProps {
    getTokenConfig: RawFormComponentType | null;
}

function ConfigurationTab(tabProps: IConfigurationTabProps) {
    const { getElementProps, hookForm } = useTemplateFormTools(tabProps);
    const { t } = useBundleTranslation('app/admin/concierge/custom-service');
    const { isDirty } = useFormState({ control: hookForm.hookFormControl });
    const [isTestingConnection, setIsTestingConnection] = useState(false);
    const [popupOpen, setPopupOpen] = useState(false);
    const [checkToken, setCheckToken] = useState(false);
    const [needToken, setNeedToken] = useState(false);
    const [testConnectionResponse, setTestConnectionResponse] = useState<ITestConnectionResponse>();
    const authType = hookForm.hookFormWatch('auth_type');
    const getTokenConfig = tabProps.getTokenConfig;

    const tokenFieldName = getTokenConfig?.props?.tokenField ?? '';
    const tokenData = useHasTokenString(tokenFieldName);

    const [variables, setVariables] = useState<IVariable[]>(() => {
        const jsonVariables = hookForm.hookFormGetValues('variables');
        try {
            const parsedValue = JSON.parse(jsonVariables);
            if (parsedValue) {
                return parsedValue.map((v: IVariable) => ({ ...v, isNew: false }));
            }
        } catch (e) {
            return [];
        }

        return [];
    });

    const [isVariablesIncluded, setIsVariablesIncluded] = useState<boolean>(false);

    useEffect(() => {
        if (!hookForm.formDidMount) {
            return;
        }

        const jsonVariables = hookForm.hookFormGetValues('variables');

        if (jsonVariables !== JSON.stringify(variables)) {
            hookForm.hookFormSetValue('variables', JSON.stringify(variables), { shouldDirty: isVariablesIncluded });
            setIsVariablesIncluded(true);
        }
    }, [variables, hookForm.formDidMount]);

    useEffect(() => {
        if (!hookForm.formDidMount) {
            return;
        }

        if (['service_account_token', 'user_oauth', 'service_principal'].includes(authType)) {
            setCheckToken(true);
        } else {
            setCheckToken(false);
        }
    }, [authType, hookForm.formDidMount]);

    useEffect(() => {
        const currentTokenValue = hookForm.hookFormGetValues(tokenFieldName);

        if (tokenData.hasToken && !currentTokenValue) {
            hookForm.hookFormSetValue(tokenFieldName, '********', { shouldDirty: false });
        }

        if (checkToken) {
            if (tokenData.hasToken) {
                setNeedToken(false);
            } else {
                setNeedToken(true);
            }
        } else {
            setNeedToken(false);
        }
    }, [checkToken, tokenData]);

    const testConnection = useCallback(async () => {
        if (isDirty) {
            alert(t('save_before_change_alert'));
            return;
        }
        const conciergeCustomServiceId = tabProps.elementProps.data['concierge_custom_service_id'];
        if (!conciergeCustomServiceId) {
            return;
        }

        setIsTestingConnection(true);
        try {
            const response = await conciergeAPI.testResourceConnection(conciergeCustomServiceId);
            setTestConnectionResponse(response);
            setPopupOpen(true);
        } catch (error) {
            setTestConnectionResponse({ error: t('test_connection_error'), status: 'FAIL' });
            setPopupOpen(true);
        } finally {
            setIsTestingConnection(false);
        }
    }, [isDirty, t, tabProps.elementProps.data]);

    return (
        <FormTabContent {...tabProps}>
            <Stack direction={'row'} sx={{ width: '100%', height: '100%' }}>
                <Stack sx={{ width: '100%', height: '100%', p: 2, pt: 4, mr: 4 }} spacing={1}>
                    <Stack spacing={1} flexShrink={0}>
                        <FormElementComponent {...getElementProps('icon')} />
                        <FormElementComponent {...getElementProps('icon_note')} />
                        <FormElementComponent {...getElementProps('supports_knowledge_graph_ind')} />
                        <Stack
                            direction={'row'}
                            sx={{ width: '100%' }}
                            justifyContent={'space-between'}
                            alignItems={'flex-end'}
                            spacing={1}
                        >
                            <Typography variant="heading" className={'heading-item'} sx={{ pt: 2, flexGrow: 1 }}>
                                <Box component={'span'} className={'heading-item__label'}>
                                    {t('configuration_section')}
                                </Box>
                            </Typography>
                            <Tooltip title={needToken ? t('get_token_to_test') : null}>
                                <Box>
                                    <Button
                                        sx={{ flexShrink: 0 }}
                                        variant={'outlined'}
                                        color={'neutral'}
                                        startIcon={<IconMi icon="arrows-left-and-right" />}
                                        onClick={testConnection}
                                        disabled={isTestingConnection || needToken}
                                    >
                                        {isTestingConnection ? t('testing_connection') : t('test_connection')}
                                    </Button>
                                </Box>
                            </Tooltip>
                        </Stack>
                        <Box width={'100%'}>
                            <StaticInfo label={t('configuration_note')} />
                        </Box>
                    </Stack>
                    <Box flexGrow={1} sx={{ display: 'flex', overflow: 'hidden' }}>
                        <ReactHookFormController
                            elementProps={prepareFormElementProps({
                                ...tabProps.elementProps,
                                ...{
                                    component: {
                                        component: 'SecureJsonEditor',
                                        name: 'json',
                                    } as RawFormComponentType,
                                },
                            })}
                        />
                    </Box>
                </Stack>
                <Variables variables={variables} setVariables={setVariables} />
            </Stack>
            {isTestingConnection && <LoadingPlaceholder />}
            <Popup
                open={popupOpen}
                onHide={() => setPopupOpen(false)}
                settings={
                    {
                        title: t('test_connection_result'),
                        needTranslation: false,
                        noCancel: true,
                        textOK: t('close'),
                    } as PopupSettings
                }
                maxWidth="popupSm"
            >
                <Stack spacing={1}>
                    <Typography fontWeight={600} fontSize={16}>
                        {testConnectionResponse?.status === 'OK'
                            ? testConnectionResponse.message
                                ? testConnectionResponse.message
                                : t('test_connection_success')
                            : testConnectionResponse?.error
                              ? testConnectionResponse?.error
                              : t('test_connection_error')}
                    </Typography>
                    <Box>
                        {testConnectionResponse?.details
                            ? parse(markdownConverter.makeHtml(testConnectionResponse.details))
                            : null}
                    </Box>
                </Stack>
            </Popup>
        </FormTabContent>
    );
}
